import { getRoot, isReadOnlyNode, types } from "@gadgetinc/mobx-quick-tree";
import path from "path";
import { FileSetCache } from "./FileSetCache";
import type { SourceFileIndexEntry } from "./IndexEntry";
import { fileIsNamespacedModelSchemaMetadata, isExecutableJsOrTsFile, schemaFileDirParts } from "./file-based-actions/filePathUtils";
import { getGadgetEnv } from "./utils";
import { last } from "lodash";

const recursivelyGetModelId = (schemaPaths: string[], candidateSegments: string[]): string | undefined => {
  if (candidateSegments.length === 0) return;
  const candidateSchemaPath = path.join("api/models", ...candidateSegments, "schema.gadget.ts");

  if (schemaPaths.includes(candidateSchemaPath)) {
    return candidateSegments.slice(-1)[0];
  } else {
    return recursivelyGetModelId(schemaPaths, candidateSegments.slice(0, -1));
  }
};

export const FilesystemIntrospector = types
  .model({})
  .views((self) => ({
    get filesWithoutProblems(): SourceFileIndexEntry[] {
      throw new Error("Not implemented");
    },
    get canHaveNamespacedModels() {
      const flags = getGadgetEnv(self).flags;
      return flags.features.metadataClientGenerator;
    },
  }))
  .volatile((self) => ({
    filesets: new FileSetCache(() => self.filesWithoutProblems, self.canHaveNamespacedModels, isReadOnlyNode(self)),
  }))
  .views((self) => ({
    maybeGetModelIdByFilePath(filePath: string) {
      const paths = filePath.split("/");

      if (!(paths[0] === "api" && paths[1] === "models")) {
        return;
      }

      const schemaPaths = self.filesets.schemaFilePaths;
      const candidateSegments = paths.slice(2).filter((segment) => segment !== "");

      const modelApiIdentifier = recursivelyGetModelId(schemaPaths, candidateSegments);

      if (!modelApiIdentifier) {
        return;
      }
      return modelApiIdentifier;
    },

    getSchemaFileByModelId(apiIdentifier: string) {
      const schemaPaths = self.filesets.schemaFilePaths;
      return schemaPaths.find((path) => {
        const dirNameParts = schemaFileDirParts(path);

        if (dirNameParts && last(dirNameParts) === apiIdentifier) {
          return true;
        } else {
          return false;
        }
      });
    },
    maybeGetModelNamespaceBySchemaFile(filePath: string) {
      if (!fileIsNamespacedModelSchemaMetadata({ path: filePath })) {
        // Not even a schema file. No namespace
        return;
      }

      const dirNameParts = schemaFileDirParts(filePath);

      if (dirNameParts && dirNameParts.length > 1) {
        // Ex: 'api/models/a/b/c/schema.gadget.ts' -> ['a', 'b']
        return dirNameParts.slice(0, -1);
      } else {
        // Empty namespace - Ex: 'api/models/modelA/schema.gadget.ts'
        return [];
      }
    },
  }))
  .views((self) => ({
    maybeGetModelNamespaceByFilePath(filePath: string) {
      const apiIdentifier = self.maybeGetModelIdByFilePath(filePath);
      if (!apiIdentifier) return;

      const schemaFile = self.getSchemaFileByModelId(apiIdentifier);
      if (!schemaFile) return;

      return self.maybeGetModelNamespaceBySchemaFile(schemaFile);
    },

    getModelNamespace(apiIdentifier: string) {
      const schemaFile = self.getSchemaFileByModelId(apiIdentifier);
      if (!schemaFile) return;

      return self.maybeGetModelNamespaceBySchemaFile(schemaFile);
    },

    isPathAModelFolder(filePath: string) {
      const schemaPaths = self.filesets.schemaFiles.map((file) => file.path);
      const candidateSchemaPath = path.join(filePath, "schema.gadget.ts");
      return schemaPaths.includes(candidateSchemaPath);
    },

    isPathInAModelFolder(filePath: string) {
      return !!self.maybeGetModelIdByFilePath(filePath);
    },
  }))
  .views((self) => ({
    isPathModelActionsFolder(path: string) {
      // Optional ending '/'
      const namespaceAllowedModelActionFolder = /^api\/models\/(.+\/)+actions\/?$/;
      const nonNamespacedModelActionFolder = /^api\/models\/(?![0-9/]+)([^/]+)\/actions\/$/;

      const modelActionFolderRegex = self.canHaveNamespacedModels ? namespaceAllowedModelActionFolder : nonNamespacedModelActionFolder;
      return modelActionFolderRegex.test(path);
    },
    isPathInModelActionsFolder(path: string) {
      const modelApiId = self.maybeGetModelIdByFilePath(path);
      const namespace = self.maybeGetModelNamespaceByFilePath(path);
      if (!modelApiId || !namespace) {
        return false;
      }

      const namespaceAllowedModelActionFolder = /^api\/models\/(.+\/)+actions\/([^/]+)$/;
      const nonNamespacedModelActionFolder = /^api\/models\/([^/]+)\/actions\/([^/]+)$/; // Cannot be subfolder in `api/models/modelApiId/actions/`
      const inModelActionFolderRegex = self.canHaveNamespacedModels ? namespaceAllowedModelActionFolder : nonNamespacedModelActionFolder;

      const currentModelActionsFolder = ["api", "models", ...namespace, modelApiId, "actions"].join("/");
      return inModelActionFolderRegex.test(path) && path.startsWith(currentModelActionsFolder);
    },
  }))
  .views((self) => ({
    /**
     * Check if a file is a model schema metadata file. Note that this allows for namespaced models.
     */
    fileIsModelSchemaMetadata(file: { path: string }) {
      return fileIsNamespacedModelSchemaMetadata(file);
    },

    maybeGetModelActionIdByFilePath(filePath: string) {
      if (!isExecutableJsOrTsFile(filePath)) {
        return;
      }

      if (!(self.isPathInAModelFolder(filePath) && self.isPathInModelActionsFolder(filePath))) {
        return;
      }

      const actionApiIdentifier = path.basename(filePath, path.extname(filePath));

      return actionApiIdentifier;
    },

    getModelFolderById(apiIdentifier: string) {
      const schemaPaths = self.filesets.schemaFilePaths;
      const match = schemaPaths.find((schemaPath) => schemaPath.split("/").slice(-2).includes(apiIdentifier));

      if (!match) return undefined;

      return path.dirname(match);
    },

    maybeGetGlobalActionIdByFilePath(filePath: string) {
      const paths = filePath.split("/");

      if (!isExecutableJsOrTsFile(filePath) || paths.length !== 3) {
        return;
      }

      // Possibly `globalActions/{action}.{valid_extension}` path structure
      if (paths[0] === "api" && paths[1] === "actions") {
        const actionApiIdentifier = path.basename(paths[2], path.extname(paths[2]));
        return actionApiIdentifier;
      }
    },
  }));
